
import React, { useEffect, useState } from "react";
import { Check, Languages, Mail, Save } from "lucide-react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import CmsTextEditor from "../../components/cms/CmsTextEditor";
import apiClient from "../../api/apiClient";
import { create_faq_question } from "../../reduxData/cms/cmsAction";

import "./components/bilingualCms.css";

const CONTENT_TYPE = "newsletter_content";

const PAGE_CONFIG = {
  title: "Newsletter Content",
  description:
    "Manage the newsletter heading and message in English and Dutch from one place.",
  headingLabel: "Newsletter heading",
  contentLabel: "Newsletter message",
};

const LANGUAGES = {
  EN: {
    label: "English",
    title: "English content",
    headingPlaceholder: "Enter English heading",
    contentPlaceholder: "Write English content...",
  },
  NL: {
    label: "Nederlands",
    title: "Nederlandse inhoud",
    headingPlaceholder: "Voer Nederlandse titel in",
    contentPlaceholder: "Schrijf Nederlandse inhoud...",
  },
};

const INITIAL_FORM = {
  content: "",
  content_nl: "",
  status: false,
};

const isEmptyHtml = (html = "") => {
  const element = document.createElement("div");
  element.innerHTML = html;

  return !element.textContent.trim();
};

const getStatusValue = (status) => {
  return (
    status === true ||
    status === 1 ||
    status === "1" ||
    status === "true"
  );
};

const getFieldName = (field, language) => {
  return language === "NL" ? `${field}_nl` : field;
};

export default function NewsLetterContent() {
  const dispatch = useDispatch();

  const { userPermissions } = useSelector((state) => state.auth);

  const canEdit = userPermissions?.cms?.edit === true;
  const canToggleStatus = userPermissions?.cms?.delete === true;

  const [activeLanguage, setActiveLanguage] = useState("EN");
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const languageConfig = LANGUAGES[activeLanguage];

  const contentKey = getFieldName("content", activeLanguage);

  /**
   * Load newsletter content from CMS.
   */
  const loadContent = async () => {
    setLoading(true);

    try {
      const response = await apiClient.get(
        `cms/content/${CONTENT_TYPE}`
      );

      const item = response.data?.data ?? null;
      const content = item?.content ?? {};

      setRecord(item);

      setForm({
        content: content.content ?? content.Content ?? "",
        content_nl: content.content_nl ?? content.Content_nl ?? "",
        status: getStatusValue(item?.status),
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        `Unable to load ${PAGE_CONFIG.title}.`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  /**
   * Update a form field and clear its validation error.
   */
  const updateField = (field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      [field]: "",
    }));
  };

  /**
   * Validate all English and Dutch fields.
   */
  const validateForm = () => {
    const validationErrors = {};

    const languages = [
      { contentKey: "content", language: "English" },
      { contentKey: "content_nl", language: "Dutch" },
    ];

    languages.forEach(({ contentKey, language }) => {
      if (isEmptyHtml(form[contentKey])) {
        validationErrors[contentKey] = `${language} content is required.`;
      }
    });

    return validationErrors;
  };

  /**
   * Switch language and focus validation on the relevant tab.
   */
  const showFirstErrorLanguage = (validationErrors) => {
    const firstError = Object.keys(validationErrors)[0];

    if (!firstError) {
      return;
    }

    setActiveLanguage(
      firstError.endsWith("_nl") ? "NL" : "EN"
    );
  };

  /**
   * Save both English and Dutch content.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      showFirstErrorLanguage(validationErrors);
      return;
    }

    setSaving(true);

    try {
      const content = {
        content: form.content,
        content_nl: form.content_nl,
      };

      await create_faq_question(
        {
          name: CONTENT_TYPE,
          type: CONTENT_TYPE,
          status: form.status,
          content,
        },
        record?.id,
        dispatch
      );

      toast.success(
        `${PAGE_CONFIG.title} saved in English and Dutch.`
      );

      await loadContent();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        `Unable to save ${PAGE_CONFIG.title}.`
      );
    } finally {
      setSaving(false);
    }
  };

  const renderLanguageTab = (language) => {
    const contentField = getFieldName("content", language);
    const isActive = activeLanguage === language;
    const hasContent = !isEmptyHtml(form[contentField]);

    return (
      <button
        key={language}
        type="button"
        role="tab"
        aria-selected={isActive}
        className={isActive ? "active" : ""}
        onClick={() => setActiveLanguage(language)}
      >
        <span>{language}</span>
        {LANGUAGES[language].label}

        {hasContent && <Check size={14} />}
      </button>
    );
  };

  return (
    <main className="bcms-page">
      <header className="bcms-header">
        <div className="bcms-header__icon">
          <Mail size={25} />
        </div>

        <div>
          <span>CMS CONTENT</span>
          <h1>{PAGE_CONFIG.title}</h1>
          <p>{PAGE_CONFIG.description}</p>
        </div>

        <div
          className={`bcms-state ${form.status ? "is-live" : ""
            }`}
        >
          <i />
          {form.status ? "Published" : "Hidden"}
        </div>
      </header>

      <form className="bcms-card" onSubmit={handleSubmit}>
        <div className="bcms-card__top">
          <div>
            <h2>Localized content</h2>
            <p>
              Keep both language versions together in one CMS record.
            </p>
          </div>

          <div className="bcms-tabs" role="tablist">
            {["EN", "NL"].map(renderLanguageTab)}
          </div>
        </div>

        {loading ? (
          <div className="bcms-loading">
            <Spinner animation="border" />
            <span>Loading content...</span>
          </div>
        ) : (
          <div className="bcms-editor-panel">
            <div className="bcms-language-label">
              <Languages size={17} />

              <strong>{languageConfig.title}</strong>

              <span>{activeLanguage}</span>
            </div>

            {/* Heading removed: only message content is required */}

            <label className="bcms-field">
              <span>{PAGE_CONFIG.contentLabel} *</span>

              <div
                className={`bcms-quill ${errors[contentKey] ? "has-error" : ""
                  }`}
              >
                <CmsTextEditor
                  value={form[contentKey]}
                  disabled={!canEdit}
                  placeholder={
                    languageConfig.contentPlaceholder
                  }
                  onChange={(value) =>
                    updateField(contentKey, value)
                  }
                />
              </div>

              {errors[contentKey] && (
                <small>{errors[contentKey]}</small>
              )}
            </label>
          </div>
        )}

        <footer className="bcms-footer">
          <div>
            <label className="bcms-switch">
              <input
                type="checkbox"
                checked={form.status}
                disabled={!canToggleStatus}
                onChange={(event) =>
                  updateField(
                    "status",
                    event.target.checked
                  )
                }
              />

              <span />

              <div>
                <strong>Content status</strong>

                <small>
                  {form.status
                    ? "Visible on the website"
                    : "Currently hidden"}
                </small>
              </div>
            </label>
          </div>

          {canEdit && (
            <button
              type="submit"
              className="btn bcms-save"
              disabled={saving || loading}
            >
              {saving ? (
                <Spinner size="sm" />
              ) : (
                <Save size={17} />
              )}

              Save both languages
            </button>
          )}
        </footer>
      </form>
    </main>
  );
}

